import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

/** Shared formatting helpers. */
export function formatNumber(n: number): string {
  return Math.round(n).toLocaleString();
}

export function formatCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return formatNumber(n);
}

type Accent = 'brand' | 'sky' | 'violet' | 'amber' | 'crimson';

const ACCENT_BADGE: Record<Accent, string> = {
  brand: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300',
  sky: 'bg-accent-sky/15 text-accent-sky',
  violet: 'bg-accent-violet/15 text-accent-violet',
  amber: 'bg-accent-amber/15 text-accent-amber',
  crimson: 'bg-accent-crimson/15 text-accent-crimson',
};

/** Hex values used for SVG fills/strokes (CSS classes don't reach SVG attrs). */
export const HEX: Record<Accent, string> = {
  brand: '#10b981',
  sky: '#38bdf8',
  violet: '#8b5cf6',
  amber: '#f59e0b',
  crimson: '#f43f5e',
};

// --- KPI card ---------------------------------------------------------------

export function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  accent = 'brand',
  alert = false,
  to,
}: {
  label: string;
  value: string;
  sub: string;
  icon: LucideIcon;
  accent?: Accent;
  alert?: boolean;
  to?: string;
}): JSX.Element {
  const cls =
    'relative block overflow-hidden rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-elevated';
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-500 dark:text-ink-400">
          {label}
        </span>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${ACCENT_BADGE[accent]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div
        className={`mt-3 font-display text-[28px] font-bold leading-none tracking-tight ${
          alert ? 'text-accent-crimson' : 'text-ink-900 dark:text-ink-50'
        }`}
      >
        {value}
      </div>
      <div className="mt-1.5 text-xs text-ink-500 dark:text-ink-400">{sub}</div>
    </>
  );
  return to ? (
    <Link to={to} className={`${cls} cursor-pointer`}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

// --- Horizontal bar list ----------------------------------------------------

export interface BarItem {
  label: string;
  value: number;
  display?: string;
  accent?: Accent;
}

export function BarList({ items, unit }: { items: BarItem[]; unit?: string }): JSX.Element {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = Math.max(2, (item.value / max) * 100);
        const accent = item.accent ?? 'brand';
        return (
          <div key={item.label}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm text-ink-700 dark:text-ink-200">{item.label}</span>
              <span className="font-mono text-xs font-medium text-ink-600 dark:text-ink-300">
                {item.display ?? formatNumber(item.value)}
                {unit ? ` ${unit}` : ''}
              </span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-ink-100 dark:bg-white/5">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${HEX[accent]}99, ${HEX[accent]})`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Donut ------------------------------------------------------------------

export interface DonutSegment {
  label: string;
  value: number;
  accent: Accent;
}

export function Donut({
  segments,
  centerLabel,
  centerValue,
}: {
  segments: DonutSegment[];
  centerLabel: string;
  centerValue: string;
}): JSX.Element {
  const size = 200;
  const stroke = 24;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const total = Math.max(
    1,
    segments.reduce((s, x) => s + x.value, 0),
  );
  let offset = 0;

  return (
    <div className="flex w-full flex-wrap items-center justify-center gap-8 sm:justify-between sm:gap-6">
      <div className="relative shrink-0">
        <svg viewBox={`0 0 ${size} ${size}`} className="h-44 w-44 -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            className="stroke-ink-100 dark:stroke-white/5"
          />
          {segments.map((s) => {
            const len = (s.value / total) * circ;
            const el = (
              <circle
                key={s.label}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={HEX[s.accent]}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
                strokeLinecap={len > 0 && len < circ ? 'round' : 'butt'}
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        {/* Centered total overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-3xl font-bold text-ink-900 dark:text-ink-50">{centerValue}</span>
          <span className="text-[11px] uppercase tracking-wide text-ink-500 dark:text-ink-400">{centerLabel}</span>
        </div>
      </div>

      <ul className="flex-1 space-y-2.5 sm:min-w-[180px]">
        {segments.map((s) => {
          const pct = Math.round((s.value / total) * 100);
          return (
            <li key={s.label} className="flex items-center gap-3 text-sm">
              <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: HEX[s.accent] }} />
              <span className="text-ink-700 dark:text-ink-200">{s.label}</span>
              <span className="ml-auto font-mono font-semibold text-ink-800 dark:text-ink-100">{s.value}</span>
              <span className="w-10 text-right font-mono text-xs text-ink-400 dark:text-ink-500">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// --- Area / line chart ------------------------------------------------------

export function AreaChart({
  points,
  markerTimes = new Set<number>(),
  accent = 'brand',
}: {
  points: { ts: string; value: number }[];
  markerTimes?: Set<number>;
  accent?: Accent;
}): JSX.Element {
  const W = 640;
  const H = 160;
  const pad = 8;
  if (points.length < 2) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-ink-400 dark:text-ink-500">
        Not enough data points to chart.
      </div>
    );
  }
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i: number) => pad + (i / (points.length - 1)) * (W - pad * 2);
  const y = (v: number) => pad + (1 - (v - min) / span) * (H - pad * 2);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - pad} L${x(0).toFixed(1)},${H - pad} Z`;
  const id = `grad-${accent}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-40 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={HEX[accent]} stopOpacity="0.28" />
          <stop offset="100%" stopColor={HEX[accent]} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line
          key={g}
          x1={pad}
          x2={W - pad}
          y1={pad + g * (H - pad * 2)}
          y2={pad + g * (H - pad * 2)}
          className="stroke-ink-100 dark:stroke-white/5"
          strokeWidth="1"
        />
      ))}
      <path d={area} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={HEX[accent]} strokeWidth="2" strokeLinejoin="round" />
      {points.map((p, i) =>
        markerTimes.has(new Date(p.ts).getTime()) ? (
          <circle
            key={p.ts}
            cx={x(i)}
            cy={y(p.value)}
            r="4"
            fill={HEX.crimson}
            stroke="#fff"
            strokeWidth="1.5"
          />
        ) : null,
      )}
    </svg>
  );
}
